// Must be first import — polyfills crypto.randomUUID() on non-HTTPS connections
import "./lib/crypto-polyfill";
// Must come before anything that creates the Supabase client — dev server only
import "./lib/dev-shared-sign-in";

import { createRoot } from "react-dom/client";
import { ThemeProvider } from "next-themes";
import { ErrorBoundary } from "./components/ErrorBoundary";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { installRequestIdInterceptor } from "./lib/request-id-interceptor";
import { shouldAutoReload, forceHardReload } from "./lib/lazyWithRetry";
import { isAnonymousSurface } from "./lib/anonymousSurface";
import { log } from "./lib/logger";

// Initialize error monitoring, analytics & request correlation
initSentry();
installRequestIdInterceptor();

const MIXPANEL_STARTUP_DELAY_MS = 4000;

const scheduleMixpanelInit = () => {
  // Mixpanel records what a customer does inside the product. A visitor reading
  // the public assistant is not inside the product, so it has nothing to record
  // and 421KB to cost them. Skipped outright rather than initialised-and-quiet,
  // because the cost being removed is the download, not the events.
  if (isAnonymousSurface(window.location.pathname)) return;

  const startMixpanel = () => {
    void import("./lib/analytics/mixpanel")
      .then(({ initMixpanel }) => initMixpanel())
      .catch((error: unknown) => {
        log.warn('Deferred Mixpanel init failed', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
  };

  window.setTimeout(startMixpanel, MIXPANEL_STARTUP_DELAY_MS);
};

scheduleMixpanelInit();

// Global safety net for chunk load errors from Vite's dynamic import preloader.
// When the app is deployed with new hashed filenames, users with stale tabs will
// fail to load chunks. This handler auto-reloads before the error reaches React.
// Production only: in dev, Vite's optimizeDeps rebuilds and stale HMR URLs fire
// the same event for benign reasons, which would wipe in-progress AI test state.
if (import.meta.env.PROD) {
  window.addEventListener('vite:preloadError', (event: Event & { payload?: Error }) => {
    log.warn('Vite preload error, auto-reloading', {
      error: (event as Event & { payload?: Error }).payload?.message || 'unknown',
    });

    if (shouldAutoReload()) {
      event.preventDefault(); // Stop propagation — we're handling via reload
      forceHardReload();
    }
    // If reload cap reached, let error propagate to ErrorBoundary's "App Updated" screen
  });
}

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary onReset={() => {
    // Navigate to origin for a clean start instead of window.location.reload(),
    // which would perpetuate a reload loop if the error recurs on mount.
    window.location.href = window.location.origin;
  }}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <App />
    </ThemeProvider>
  </ErrorBoundary>
);
